import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Printer, CalendarClock, CalendarRange, Target, ChevronsDownUp, ChevronsUpDown, RefreshCw, Eye, EyeOff, Undo2, Library, CalendarPlus } from "lucide-react";
import { useAuth } from "../../App";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import * as api from "../../lib/api";
import { downloadFromApi } from "../../lib/download";
import { GoalCard } from "./GoalCard";
import { AddGoalDialog } from "./AddGoalDialog";
import { DueDatesDialog, ReportingPeriodsDialog, TemplatesLibraryDialog } from "./GoalsDialogs";
import { ImportGoalsDialog } from "./ImportGoalsDialog";
import { Upload } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return isNaN(d) ? iso : d.toLocaleDateString();
};

function DueChips({ student, onEdit }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="outline" className={student.iep_meeting_due_date ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "text-slate-400"}>
        IEP Meeting: {student.iep_meeting_due_date ? fmtDate(student.iep_meeting_due_date) : "not set"}
      </Badge>
      <Badge variant="outline" className={student.goal_report_due_date ? "border-teal-200 bg-teal-50 text-teal-700" : "text-slate-400"}>
        Goal Report: {student.goal_report_due_date ? fmtDate(student.goal_report_due_date) : "not set"}
      </Badge>
      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onEdit} data-testid={`set-due-dates-${student.id}`}>
        <CalendarClock className="w-3.5 h-3.5 mr-1" />Set dates
      </Button>
    </div>
  );
}

export function GoalsView({ studentId = null }) {
  const { isAdmin } = useAuth();
  const [meta, setMeta] = useState({ goal_types: [], reporting_periods: [], students: [], goal_templates: [], current_school_year: "" });
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ goal_type: "", reporting_period_id: "", status: "" });
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addForStudent, setAddForStudent] = useState(studentId);
  const [dueStudent, setDueStudent] = useState(null);
  const [periodsOpen, setPeriodsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [confirmRollAll, setConfirmRollAll] = useState(false);
  const [rolling, setRolling] = useState(false);
  const [lastBatchId, setLastBatchId] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [confirmYearRoll, setConfirmYearRoll] = useState(false);
  const [yearRolling, setYearRolling] = useState(false);
  const [yearBatchId, setYearBatchId] = useState(null);

  const collapseAll = () => setCollapsedIds(new Set((goals || []).map((g) => g.id)));
  const expandAll = () => setCollapsedIds(new Set());
  const toggleCollapsed = (id) => setCollapsedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const loadMeta = useCallback(async () => {
    try { const r = await api.getGoalsMeta(); setMeta(r.data); } catch (e) { /* noop */ }
  }, []);

  const loadGoals = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (studentId) params.student_id = studentId;
      if (filters.goal_type) params.goal_type = filters.goal_type;
      if (filters.reporting_period_id) params.reporting_period_id = filters.reporting_period_id;
      if (filters.status) params.status = filters.status;
      const r = await api.getGoals(params);
      setGoals(r.data);
    } catch (e) { toast.error("Failed to load goals"); }
    finally { setLoading(false); }
  }, [studentId, filters]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadGoals(); }, [loadGoals]);

  const refresh = () => { loadGoals(); loadMeta(); };

  const studentById = useMemo(() => Object.fromEntries((meta.students || []).map((s) => [s.id, s])), [meta.students]);
  const singleStudent = studentId ? studentById[studentId] : null;

  // Student-file mode: archived goals are hidden by default (toggle to review them).
  const visibleGoals = useMemo(() => {
    if (!studentId) return goals;
    return showArchived ? goals : goals.filter((g) => g.status !== "archived");
  }, [goals, studentId, showArchived]);
  const archivedCount = useMemo(() => (studentId ? goals.filter((g) => g.status === "archived").length : 0), [goals, studentId]);
  const activeCount = useMemo(() => (studentId ? goals.filter((g) => g.status === "active").length : 0), [goals, studentId]);

  const doRollAllActive = async () => {
    setRolling(true);
    try {
      const r = await api.rolloverActiveGoals(studentId);
      toast.success(`Rolled over ${r.data.rolled} active goal(s) to next year`);
      setLastBatchId(r.data.batch_id || null);
      setConfirmRollAll(false);
      refresh();
    } catch (e) { toast.error("Failed to roll over goals"); }
    finally { setRolling(false); }
  };

  const undoRollover = async () => {
    if (!lastBatchId) return;
    try {
      const r = await api.undoRolloverBatch(lastBatchId);
      toast.success(`Undid rollover — ${r.data.undone} goal(s) restored`);
      setLastBatchId(null);
      refresh();
    } catch (e) { toast.error("Failed to undo rollover"); }
  };

  const doYearRollover = async () => {
    setYearRolling(true);
    try {
      const r = await api.rolloverAllActiveGoals();
      if (!r.data.rolled) {
        toast.info("No active goals to roll over");
      } else {
        toast.success(`Rolled over ${r.data.rolled} goal(s) across ${r.data.students_affected} student(s)`);
        setYearBatchId(r.data.batch_id || null);
      }
      setConfirmYearRoll(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Admins only" : "Failed to roll over goals");
    } finally { setYearRolling(false); }
  };

  const undoYearRollover = async () => {
    if (!yearBatchId) return;
    try {
      const r = await api.undoRolloverBatch(yearBatchId);
      toast.success(`Undid year rollover — ${r.data.undone} goal(s) restored`);
      setYearBatchId(null);
      refresh();
    } catch (e) { toast.error("Failed to undo year rollover"); }
  };

  // Batch reports (all-students mode): students whose Goal Report is due this month.
  const dueThisMonth = useMemo(() => {
    if (studentId) return [];
    const now = new Date();
    return (meta.students || []).filter((s) => {
      const d = s.goal_report_due_date;
      if (!d) return false;
      const dt = new Date(d + "T00:00:00");
      return !isNaN(dt) && dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth();
    });
  }, [meta.students, studentId]);

  const downloadBatchDueThisMonth = async () => {
    if (dueThisMonth.length === 0) return;
    try {
      const params = new URLSearchParams({ student_ids: dueThisMonth.map((s) => s.id).join(",") });
      if (filters.goal_type) params.set("goal_type", filters.goal_type);
      if (filters.reporting_period_id) params.set("reporting_period_id", filters.reporting_period_id);
      if (filters.status) params.set("status", filters.status);
      await downloadFromApi(`/api/goals/report-pdf-batch?${params.toString()}`, "goal_reports_due_this_month.zip");
    } catch (e) { toast.error("Failed to generate reports"); }
  };

  const printReport = async (sid) => {
    try {
      const params = new URLSearchParams({ student_id: sid });
      if (filters.goal_type) params.set("goal_type", filters.goal_type);
      if (filters.reporting_period_id) params.set("reporting_period_id", filters.reporting_period_id);
      if (filters.status) params.set("status", filters.status);
      const name = (studentById[sid]?.name || "student").replace(/\s+/g, "_");
      await downloadFromApi(`/api/goals/report-pdf?${params.toString()}`, `goal_report_${name}.pdf`);
    } catch (e) { toast.error("Failed to generate report"); }
  };

  // Group goals by student (all-students mode).
  const grouped = useMemo(() => {
    if (studentId) return null;
    const ql = q.trim().toLowerCase();
    const map = {};
    for (const g of goals) {
      if (ql && !(g.student_name || "").toLowerCase().includes(ql)) continue;
      (map[g.student_id] = map[g.student_id] || []).push(g);
    }
    return map;
  }, [goals, studentId, q]);

  const FilterBar = (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={filters.goal_type || "all"} onValueChange={(v) => setFilters((p) => ({ ...p, goal_type: v === "all" ? "" : v }))}>
        <SelectTrigger className="w-40 h-9" data-testid="filter-goal-type"><SelectValue placeholder="Goal type" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All goal types</SelectItem>
          {(meta.goal_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.reporting_period_id || "all"} onValueChange={(v) => setFilters((p) => ({ ...p, reporting_period_id: v === "all" ? "" : v }))}>
        <SelectTrigger className="w-52 h-9" data-testid="filter-period"><SelectValue placeholder="Reporting period" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Full history (all periods)</SelectItem>
          {(meta.reporting_periods || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.status || "all"} onValueChange={(v) => setFilters((p) => ({ ...p, status: v === "all" ? "" : v }))}>
        <SelectTrigger className="w-36 h-9" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="met">Met</SelectItem>
          <SelectItem value="rolled_over">Rolled Over</SelectItem>
          <SelectItem value="draft">Draft (Unofficial)</SelectItem>
          <SelectItem value="archived">Archived</SelectItem>
        </SelectContent>
      </Select>
      {!studentId && (
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search student…" className="w-44 h-9" data-testid="filter-student-search" />
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {FilterBar}
        <div className="flex items-center gap-2">
          {goals.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={collapseAll} data-testid="collapse-all-goals-btn">
                <ChevronsDownUp className="w-4 h-4 mr-1" />Collapse All
              </Button>
              <Button variant="outline" size="sm" onClick={expandAll} data-testid="expand-all-goals-btn">
                <ChevronsUpDown className="w-4 h-4 mr-1" />Expand All
              </Button>
            </>
          )}
          {!studentId && (
            <>
              <Button variant="outline" size="sm" onClick={downloadBatchDueThisMonth} disabled={dueThisMonth.length === 0} data-testid="batch-reports-due-btn">
                <Printer className="w-4 h-4 mr-1" />Reports due this month{dueThisMonth.length > 0 ? ` (${dueThisMonth.length})` : ""}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setTemplatesOpen(true)} data-testid="template-library-btn">
                <Library className="w-4 h-4 mr-1" />Templates{(meta.goal_templates || []).length > 0 ? ` (${meta.goal_templates.length})` : ""}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPeriodsOpen(true)} data-testid="manage-periods-btn">
                <CalendarRange className="w-4 h-4 mr-1" />Reporting Periods
              </Button>
              {isAdmin() && (
                <Button variant="outline" size="sm" onClick={() => setConfirmYearRoll(true)} data-testid="year-rollover-btn">
                  <CalendarPlus className="w-4 h-4 mr-1" />Year Rollover
                </Button>
              )}
              {yearBatchId && (
                <Button variant="outline" size="sm" className="text-amber-700 border-amber-300" onClick={undoYearRollover} data-testid="undo-year-rollover-btn">
                  <Undo2 className="w-4 h-4 mr-1" />Undo year rollover
                </Button>
              )}
            </>
          )}
          {studentId && (
            <>
              {archivedCount > 0 && (
                <Button variant="outline" size="sm" onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived-btn">
                  {showArchived ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                  {showArchived ? "Hide archived" : `Show archived (${archivedCount})`}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setConfirmRollAll(true)} disabled={activeCount === 0} data-testid="rollover-all-btn">
                <RefreshCw className="w-4 h-4 mr-1" />Roll over all active{activeCount > 0 ? ` (${activeCount})` : ""}
              </Button>
              {lastBatchId && (
                <Button variant="outline" size="sm" className="text-amber-700 border-amber-300" onClick={undoRollover} data-testid="undo-rollover-btn">
                  <Undo2 className="w-4 h-4 mr-1" />Undo rollover
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setDueStudent(singleStudent)} data-testid="single-set-dates-btn">
                <CalendarClock className="w-4 h-4 mr-1" />Due Dates
              </Button>
              <Button variant="outline" size="sm" onClick={() => printReport(studentId)} data-testid="single-print-report-btn">
                <Printer className="w-4 h-4 mr-1" />Print Report
              </Button>
            </>
          )}
          <Button size="sm" onClick={() => { setAddForStudent(studentId); setAddOpen(true); }} data-testid="add-goal-btn">
            <Plus className="w-4 h-4 mr-1" />Add Goal
          </Button>
          {meta.import_enabled && (
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} data-testid="import-goals-btn">
              <Upload className="w-4 h-4 mr-1" />Import from Document
            </Button>
          )}
        </div>
      </div>

      {/* Single-student due chips */}
      {studentId && singleStudent && (
        <Card><CardContent className="p-3"><DueChips student={singleStudent} onEdit={() => setDueStudent(singleStudent)} /></CardContent></Card>
      )}

      {loading ? (
        <p className="text-sm text-slate-400">Loading goals…</p>
      ) : studentId ? (
        goals.length === 0 ? (
          <EmptyState onAdd={() => { setAddForStudent(studentId); setAddOpen(true); }} />
        ) : visibleGoals.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">
            All goals for this student are archived.{" "}
            <button className="text-[#00305E] underline" onClick={() => setShowArchived(true)} data-testid="show-archived-inline">Show archived</button> to view them.
          </div>
        ) : (
          <div className="space-y-3">
            {visibleGoals.map((g) => <GoalCard key={g.id} goal={g} periods={meta.reporting_periods} onChanged={refresh} iepDueDate={singleStudent?.iep_meeting_due_date || ""} collapsed={collapsedIds.has(g.id)} onToggleCollapsed={() => toggleCollapsed(g.id)} />)}
          </div>
        )
      ) : (
        // All-students mode
        Object.keys(grouped || {}).length === 0 ? (
          <EmptyState onAdd={() => { setAddForStudent(null); setAddOpen(true); }} />
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped)
              .sort((a, b) => (studentById[a[0]]?.name || "").localeCompare(studentById[b[0]]?.name || ""))
              .map(([sid, sgoals]) => {
                const s = studentById[sid] || { id: sid, name: sgoals[0]?.student_name || "Unknown" };
                return (
                  <div key={sid} className="space-y-2" data-testid={`goal-student-section-${sid}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-2">
                      <div className="flex flex-wrap items-center gap-3">
                        <Link to={`/students/${sid}?tab=goals`} className="font-semibold text-[#00305E] hover:underline">{s.name}</Link>
                        <DueChips student={s} onEdit={() => setDueStudent(s)} />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => printReport(sid)} data-testid={`print-report-${sid}`}><Printer className="w-3.5 h-3.5 mr-1" />Print</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setAddForStudent(sid); setAddOpen(true); }} data-testid={`add-goal-for-${sid}`}><Plus className="w-3.5 h-3.5 mr-1" />Add goal</Button>
                      </div>
                    </div>
                    {sgoals.map((g) => <GoalCard key={g.id} goal={g} periods={meta.reporting_periods} onChanged={refresh} iepDueDate={s.iep_meeting_due_date || ""} collapsed={collapsedIds.has(g.id)} onToggleCollapsed={() => toggleCollapsed(g.id)} />)}
                  </div>
                );
              })}
          </div>
        )
      )}

      <AddGoalDialog
        open={addOpen} onOpenChange={setAddOpen}
        studentId={addForStudent}
        students={meta.students} goalTypes={meta.goal_types} periods={meta.reporting_periods}
        templates={meta.goal_templates || []} onTemplatesChanged={loadMeta}
        onCreated={refresh}
      />
      <DueDatesDialog open={!!dueStudent} onOpenChange={(v) => { if (!v) setDueStudent(null); }} student={dueStudent} onSaved={refresh} />
      <ReportingPeriodsDialog open={periodsOpen} onOpenChange={setPeriodsOpen} periods={meta.reporting_periods} onChanged={loadMeta} />
      <TemplatesLibraryDialog open={templatesOpen} onOpenChange={setTemplatesOpen} templates={meta.goal_templates || []} onChanged={loadMeta} />
      <ImportGoalsDialog open={importOpen} onOpenChange={setImportOpen} studentId={studentId} students={meta.students} periods={meta.reporting_periods} onImported={refresh} />

      <AlertDialog open={confirmRollAll} onOpenChange={setConfirmRollAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Roll over all active goals?</AlertDialogTitle>
            <AlertDialogDescription>
              This creates a fresh next-school-year copy of each of this student's {activeCount} active goal(s), carrying full note history. Each original is marked "Rolled Over" and kept as history. Draft, Met, and Archived goals are untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRollAllActive} disabled={rolling} data-testid="confirm-rollover-all">
              {rolling ? "Rolling over…" : "Roll over all"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmYearRoll} onOpenChange={setConfirmYearRoll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Roll over ALL students' active goals?</AlertDialogTitle>
            <AlertDialogDescription>
              This start-of-year action creates a fresh next-school-year copy of every current student's Active goal (carrying full note history) and marks each original "Rolled Over". Draft, Met, and Archived goals are untouched. You can undo this immediately afterward.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doYearRollover} disabled={yearRolling} data-testid="confirm-year-rollover">
              {yearRolling ? "Rolling over…" : "Roll over everyone"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Target className="w-10 h-10 text-slate-300 mb-2" />
      <p className="text-slate-500">No goals yet.</p>
      <Button className="mt-3" size="sm" onClick={onAdd}><Plus className="w-4 h-4 mr-1" />Add the first goal</Button>
    </div>
  );
}
